import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";

const ITEM_COLORS = {
  campaign: { bar: "border-blue-500", bg: "bg-blue-50", text: "text-blue-700" },
  leave: { bar: "border-amber-500", bg: "bg-amber-50", text: "text-amber-700" },
  default: { bar: "border-zinc-400", bg: "bg-zinc-50", text: "text-zinc-700" },
};

const HEALTH_COLORS = {
  green: "border-emerald-500 bg-emerald-50 text-emerald-700",
  amber: "border-amber-500 bg-amber-50 text-amber-700",
  red: "border-red-500 bg-red-50 text-red-700",
};

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0); }
function fmtDate(d) {
  // TZ-safe: format local date as YYYY-MM-DD (avoid toISOString which uses UTC)
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function parseLocalDate(iso) {
  // Parse "YYYY-MM-DD" as local midnight (avoid Date('YYYY-MM-DD') which uses UTC)
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1);
}

export default function CalendarPage() {
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState("month"); // month | week | day
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let from, to;
    if (view === "month") {
      from = startOfMonth(cursor);
      to = endOfMonth(cursor);
    } else if (view === "week") {
      const dow = cursor.getDay();
      from = new Date(cursor); from.setDate(cursor.getDate() - dow);
      to = new Date(from); to.setDate(from.getDate() + 6);
    } else {
      from = new Date(cursor);
      to = new Date(cursor);
    }
    api.get("/calendar/events", { params: { from_date: fmtDate(from), to_date: fmtDate(to) } }).then((r) => setEvents(r.data));
  }, [cursor, view]);

  const monthGrid = useMemo(() => {
    const start = startOfMonth(cursor);
    const end = endOfMonth(cursor);
    const startDow = start.getDay();
    const days = [];
    for (let i = 0; i < startDow; i++) {
      const d = new Date(start);
      d.setDate(d.getDate() - (startDow - i));
      days.push({ date: d, current: false });
    }
    for (let i = 1; i <= end.getDate(); i++) {
      days.push({ date: new Date(cursor.getFullYear(), cursor.getMonth(), i), current: true });
    }
    while (days.length % 7 !== 0) {
      const last = days[days.length - 1].date;
      const d = new Date(last);
      d.setDate(d.getDate() + 1);
      days.push({ date: d, current: false });
    }
    return days;
  }, [cursor]);

  const eventsByDate = useMemo(() => {
    const map = {};
    for (const ev of events) {
      try {
        const s = parseLocalDate(ev.start_datetime);
        const e = parseLocalDate(ev.end_datetime);
        for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
          const k = fmtDate(d);
          (map[k] = map[k] || []).push(ev);
        }
      } catch {}
    }
    return map;
  }, [events]);

  const navigate = (delta) => {
    const next = new Date(cursor);
    if (view === "month") next.setMonth(cursor.getMonth() + delta);
    else if (view === "week") next.setDate(cursor.getDate() + delta * 7);
    else next.setDate(cursor.getDate() + delta);
    setCursor(next);
  };

  return (
    <div className="space-y-5" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">Unified Calendar</p>
          <h2 className="font-display text-3xl font-bold tracking-tight mt-1">{cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}</h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-zinc-200 rounded-md overflow-hidden" data-testid="calendar-view-switcher">
            {["month", "week", "day"].map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                data-testid={`view-${v}`}
                className={`px-3 py-1.5 text-xs capitalize ${view === v ? "bg-zinc-950 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}
              >
                {v}
              </button>
            ))}
          </div>
          <Button variant="outline" size="icon" onClick={() => navigate(-1)} data-testid="calendar-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())} data-testid="calendar-today">Today</Button>
          <Button variant="outline" size="icon" onClick={() => navigate(1)} data-testid="calendar-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>

      <Card className="overflow-hidden">
        {view === "month" && (
          <div data-testid="calendar-month-view">
            <div className="grid grid-cols-7 text-xs uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b border-zinc-200">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="px-3 py-2 font-semibold border-r border-zinc-200 last:border-r-0">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {monthGrid.map((cell, i) => {
                const k = fmtDate(cell.date);
                const dayEvents = eventsByDate[k] || [];
                const isToday = k === fmtDate(new Date());
                return (
                  <div key={i} className={`border-r border-b border-zinc-100 last:border-r-0 min-h-[110px] p-1.5 ${cell.current ? "bg-white" : "bg-zinc-50/50"}`} data-testid={`calendar-day-${k}`}>
                    <div className={`text-xs font-mono mb-1 ${isToday ? "inline-flex items-center justify-center h-5 w-5 rounded-full bg-zinc-950 text-white" : cell.current ? "text-zinc-700" : "text-zinc-400"}`}>
                      {cell.date.getDate()}
                    </div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 3).map((ev, idx) => {
                        const colors = ev.item_type === "leave"
                          ? ITEM_COLORS.leave
                          : (HEALTH_COLORS[ev.health] ? null : ITEM_COLORS.campaign);
                        const cls = ev.item_type === "campaign" && HEALTH_COLORS[ev.health]
                          ? `${HEALTH_COLORS[ev.health]} border-l-2`
                          : `${colors.bar} ${colors.bg} ${colors.text} border-l-2`;
                        return (
                          <div key={idx} className={`text-[11px] px-1.5 py-0.5 truncate rounded-r ${cls}`} title={ev.title} data-testid={`calendar-event-${ev.id}`}>
                            {ev.title}
                          </div>
                        );
                      })}
                      {dayEvents.length > 3 && (
                        <div className="text-[10px] text-zinc-500 px-1.5">+{dayEvents.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view !== "month" && (
          <div className="p-5 space-y-2" data-testid={`calendar-${view}-view`}>
            <h3 className="font-display text-base font-semibold">
              {view === "week" ? "This week's items" : `Items for ${fmtDate(cursor)}`}
            </h3>
            {events.length === 0 && <p className="text-sm text-zinc-500">No items scheduled.</p>}
            <div className="space-y-2">
              {events.map((ev) => {
                const cls = ev.item_type === "campaign" && HEALTH_COLORS[ev.health]
                  ? HEALTH_COLORS[ev.health]
                  : `${ITEM_COLORS[ev.item_type]?.bg || "bg-zinc-50"} ${ITEM_COLORS[ev.item_type]?.text || "text-zinc-700"} border-zinc-200`;
                return (
                  <div key={ev.id} className={`p-3 rounded-md border-l-4 border ${cls}`} data-testid={`calendar-list-${ev.id}`}>
                    <div className="font-medium text-sm">{ev.title}</div>
                    <div className="text-xs opacity-80 mt-0.5">
                      {ev.item_type} · {ev.start_datetime.slice(0, 10)} → {ev.end_datetime.slice(0, 10)}
                      {ev.city ? ` · ${ev.city}` : ""}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 text-xs text-zinc-600">
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-emerald-500" /> On track</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-amber-500" /> At risk / leave</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-red-500" /> Delayed</span>
        <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-blue-500" /> Campaign</span>
      </div>
    </div>
  );
}
